import type {
  MatchHubMockData,
  MatchHubLineup,
  MatchHubLineupPlayer,
} from '../types/matchHub.types';

export interface RawApiLineup {
  team?: { id?: number; name?: string };
  formation?: string;
  startXI?: Array<{ player?: { id?: number; name?: string; number?: number; pos?: string } }>;
  substitutes?: Array<{ player?: { id?: number; name?: string; number?: number; pos?: string } }>;
  coach?: { id?: number; name?: string };
}

const NAME_SUFFIXES = new Set(['jr', 'jr.', 'sr', 'sr.', 'ii', 'iii', 'iv']);

// Surname particles that belong to the last name (e.g. "van der Ven", "De Gea").
const NAME_PARTICLES = new Set([
  'van', 'von', 'de', 'der', 'den', 'del', 'di', 'da', 'dos', 'das', 'do',
  'la', 'le', 'el', 'bin', 'al', 'ter', 'te',
]);

const POSITION_LABELS: Record<string, string> = {
  G: 'Goalkeeper',
  D: 'Defender',
  M: 'Midfielder',
  F: 'Forward',
};

// Render as first-name initial + surname, keeping compound surnames intact:
// "Dirk van der Ven" → "D. van der Ven", "David De Gea" → "D. De Gea",
// "Bruno Fernandes" → "B. Fernandes". Single-word names are returned as-is.
function shortPlayerName(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length <= 1) return fullName;

  // Drop a trailing suffix (Jr., III, …) so it doesn't end up as the surname.
  let end = parts.length;
  if (NAME_SUFFIXES.has(parts[end - 1].toLowerCase()) && end > 2) end -= 1;

  // Surname starts at the first particle after the first name, otherwise the last word.
  let surnameStart = end - 1;
  for (let i = 1; i < end; i++) {
    if (NAME_PARTICLES.has(parts[i].toLowerCase())) {
      surnameStart = i;
      break;
    }
  }

  const initial = `${parts[0].charAt(0).toUpperCase()}.`;
  const surname = parts.slice(surnameStart, end).join(' ');
  return `${initial} ${surname}`;
}

function mapApiPlayers(
  apiPlayers: Array<{ player?: { id?: number; name?: string; number?: number; pos?: string } }> | undefined,
): MatchHubLineupPlayer[] {
  if (!apiPlayers) return [];
  return apiPlayers
    .map((entry): MatchHubLineupPlayer | null => {
      const p = entry?.player;
      if (!p?.name) return null;
      return {
        id: String(p.id ?? Math.random()),
        name: shortPlayerName(p.name),
        number: p.number ?? 0,
        ...(p.pos ? { position: POSITION_LABELS[p.pos] ?? p.pos } : {}),
      };
    })
    .filter((p): p is MatchHubLineupPlayer => p !== null);
}

function mapApiLineup(raw: RawApiLineup | undefined): MatchHubLineup {
  return {
    formation: raw?.formation ?? '',
    starting: mapApiPlayers(raw?.startXI),
    bench: mapApiPlayers(raw?.substitutes),
  };
}

export function mapLineupsToHub(
  payload: { lineups: RawApiLineup[] | null } | null,
  homeExtId: number | null,
  awayExtId: number | null,
): {
  lineups: MatchHubMockData['lineups'];
  coaches: MatchHubMockData['coaches'];
} {
  const empty = {
    lineups: { isExpected: false, home: { formation: '', starting: [], bench: [] }, away: { formation: '', starting: [], bench: [] } },
    coaches: { home: { name: '' }, away: { name: '' } },
  };

  if (!payload?.lineups?.length) return empty;

  const homeRaw = payload.lineups.find((l) => homeExtId != null && l.team?.id === homeExtId);
  const awayRaw = payload.lineups.find((l) => awayExtId != null && l.team?.id === awayExtId);

  const hasData = homeRaw != null || awayRaw != null;

  return {
    lineups: {
      isExpected: false,
      home: mapApiLineup(homeRaw),
      away: mapApiLineup(awayRaw),
    },
    coaches: {
      home: { name: hasData ? (homeRaw?.coach?.name ?? '') : '' },
      away: { name: hasData ? (awayRaw?.coach?.name ?? '') : '' },
    },
  };
}
